import { isDefined } from '@sgme/fp';

import type { WebapiEndpointBuilder } from '@/api/webapi.ts';
import { selectCashTile } from '@/store/cash/selectors/tile.ts';
import type { CashProductName, FxCashForward, FxCashNdf, FxCashSpot } from '@/store/cash/state.ts';
import type { AppState } from '@/store/store.ts';
import type { TradeCaptureResponseErrors, TradeCaptureResponseWarnings } from '@/types/trade-capture.ts';
import type { ObjectWithUndefinedPropertiesNullable } from '@/utils/typescript.ts';

export type CheckCashParams = CheckSpotParams | CheckForwardParams | CheckNdfParams;

export type CheckSpotParams = {
  tileId: string;
  type: 'FxSpot';
  isNonDeliverable?: boolean;
  properties: ObjectWithUndefinedPropertiesNullable<Partial<FxCashSpot['properties']>>;
};

export type CheckForwardParams = {
  tileId: string;
  type: 'FxFwd';
  isNonDeliverable?: boolean;
  properties: ObjectWithUndefinedPropertiesNullable<Partial<FxCashForward['properties']>>;
};

export type CheckNdfParams = {
  tileId: string;
  type: 'FxNdf';
  isNonDeliverable?: boolean;
  properties: ObjectWithUndefinedPropertiesNullable<Partial<FxCashNdf['properties']>>;
};

export type CheckCashBody = {
  sessionId: string;
  tileId: string;
  request: {
    idVersion: number;
    changedFields: {
      currencyPair?: string;
      legs: {
        0: {
          productName: string;
          isNonDeliverable?: boolean;

          // update near amount 1 when the first currency is selected,
          // and 2 when the second currency is selected
          // not both at the same time
          amount1String?: string | null;
          amount2String?: string | null;

          maturityDateString?: string;

          fixingCurrency?: string | null;
          // xCurrency?: string;
          fixingSource?: string | null;
          sndFixingSource?: string | null;
        };
      };
    };
  };
}[];

export type CheckCashResponse = Record<string, CheckedCash>;

export type CheckedCash = {
  idVersion: number;
  changedFields: {
    currencyPair?: string;
    currency1?: string;
    currency2?: string;

    isPriceObsolete?: boolean;
    isProductObsolete?: boolean;
    isReadyToPrice?: boolean;

    legs: Record<string, CheckedCashLeg>;
  };
  warnings: TradeCaptureResponseWarnings | null;
  errors: TradeCaptureResponseErrors | null;
};

type CheckedCashLeg = {
  productName?: CashProductName;

  isPriceObsolete?: boolean;
  isProductObsolete?: boolean;
  isReadyToPrice?: boolean;

  amount1?: number | null;
  amount1String?: string;
  amount2?: number | null;
  amount2String?: string;

  maturityDate?: string | null;
  maturityDateString?: string;
  maturityDateTenor?: string | null;

  isNonDeliverable?: boolean;

  fixingDate?: string;

  fixingCurrency?: string;
  possibleFixingCurrencies?: {
    default: string;
    possible: string[];
  };

  isCrossed?: boolean;
  xCurrency?: string;
  negotiatedCurrency?: string;
  possibleXCurrencies?: {
    default: string;
    possible: string[];
  };

  fixingSource?: string;
  possibleFixingSources?: {
    default: string;
    possible: string[];
  };

  sndFixingSource?: string;
  possibleSndFixingSources?: {
    default: string;
    possible: string[];
  };
};

export const checkCashByTradeCapture = (builder: WebapiEndpointBuilder) => {
  return builder.mutation<CheckCashResponse, CheckCashParams>({
    queryFn: async (params, api, _extraOptions, baseQuery) => {
      const state = api.getState() as AppState;
      const body = getCheckCashBody(params, state);

      const result = await baseQuery({
        url: '/tradeCaptureBatch/FxSpot',
        method: 'POST',
        body,
        timeout: 10_000, // 10 seconds
      });

      if (result.error) {
        return { error: result.error };
      }

      return { data: result.data as CheckCashResponse };
    },
  });
};

const getCheckCashBody = (params: CheckCashParams, state: AppState): CheckCashBody => {
  const cash = selectCashTile(state, params.tileId);

  if (!cash?.tradeCapture) {
    throw new Error(`Cash tile ${params.tileId} not found or has no trade capture data`);
  }

  return [
    {
      sessionId: cash.tradeCapture.sessionId,
      tileId: params.tileId,
      request: {
        idVersion: cash.tradeCapture.version + 1,

        changedFields: {
          currencyPair: params.properties.currencyPair ?? undefined,

          legs: {
            0: {
              productName: params.type,
              isNonDeliverable: params.isNonDeliverable,

              amount1String:
                params.properties.notionalCurrencyIndex === 1
                  ? isDefined(params.properties.notional)
                    ? String(params.properties.notional)
                    : null
                  : undefined,
              amount2String:
                params.properties.notionalCurrencyIndex === 2
                  ? isDefined(params.properties.notional)
                    ? String(params.properties.notional)
                    : null
                  : undefined,

              maturityDateString:
                params.isNonDeliverable === false
                  ? 'SP'
                  : (params.properties.maturityDate ?? params.properties.maturityDateTenor ?? undefined),

              fixingCurrency: params.type === 'FxNdf' ? params.properties.fixingCurrency : undefined,
              fixingSource: params.type === 'FxNdf' ? params.properties.fixingSource : undefined,
              sndFixingSource: params.type === 'FxNdf' ? params.properties.sndFixingSource : undefined,
            },
          },
        },
      },
    },
  ];
};
